import { Fragment, FunctionComponent, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  GiftPlan,
  isGiftPlan
} from "rivals/components/pages/User/Subscriptions/types";
import { ADDON_MODAL_CONSTANTS } from "rivals/components/shared/AddonOptions/constants";
import { getPromotion } from "rivals/redux/promotion/selectors";
import { getTax } from "rivals/redux/rails/actions";
import { getSubscriptionPricingConstants } from "rivals/redux/rails/selectors";
import { getGiftTax } from "rivals/redux/registration/actions";
import { ZIP_CODE_REGEX } from "rivals/services/billing/types";
import {
  CENTS_PER_DOLLAR,
  PLANS,
  ROOT_SITE_IDENTIFIER
} from "rivals/shared/constants";
import { AddOn } from "rivals/shared/interfaces/AddOn";
import { Site } from "rivals/shared/interfaces/Site";
import { TaxAddress } from "rivals/shared/interfaces/User";
import { formatCurrency, getDiscount } from "rivals/shared/utils/money";
import { setData } from "rivals/shared/utils/qa";
import styles from "./SubscriptionPricing.module.scss";
import strings from "./strings";
import { Props } from "./types";

const {
  annualSubscription,
  billingToday,
  creditCard,
  estimatedTax,
  free,
  includedWith,
  monthlySubscription,
  payPalAccount,
  paymentMethod,
  prorated,
  readOnlyAccess,
  total
} = strings;

const SubscriptionPricing: FunctionComponent<Props> = ({
  addOn,
  address,
  bundle,
  isPayPal,
  isValid,
  planType,
  promoCodeValue = "",
  proratedPrice,
  showAddOnOnly = false,
  selectedSite,
  tax,
  upgradingAddOns
}) => {
  const pricingConstants = useSelector(getSubscriptionPricingConstants);

  if (!pricingConstants) {
    return null;
  }

  const promotion = useSelector(getPromotion);

  const dispatch = useDispatch();

  // Request tax logic START
  useEffect(() => {
    if (
      selectedSite &&
      address?.zip &&
      address?.state &&
      ZIP_CODE_REGEX.test(address.zip)
    ) {
      // Not a gift
      if (planType === PLANS.ANNUAL || planType === PLANS.MONTHLY) {
        requestTax(planType, selectedSite, address);
        // A gift
      } else if (isGiftPlan(planType)) {
        requestGiftTax(planType, selectedSite, address);
      }
    }
  }, [address, addOn, planType, promotion, selectedSite]);
  // Request tax logic END

  // Price calculation logic START
  // TODO: https://griosf.atlassian.net/browse/RVLS-7859
  const { annualCostDollars, monthlyCostDollars } = pricingConstants;

  // Default to 0 if no add on is present or if we are in showAddOnOnly mode
  const addOnPrice = addOn && !showAddOnOnly ? Number(addOn.price) : 0;

  // Set defaults and override based on props
  const planPricing = { addOnPrice, price: 0, subscription: "" };

  if (showAddOnOnly && addOn) {
    // Special case for only showing AddOn pricing
    planPricing.price = proratedPrice || addOnPrice;
    planPricing.subscription = `${addOn.lineItem}`;
  } else if (planType === PLANS.ANNUAL || planType === PLANS.MONTHLY) {
    // Not a gift
    planPricing.price = Number(
      planType === PLANS.ANNUAL ? annualCostDollars : monthlyCostDollars
    );
    planPricing.subscription =
      planType === PLANS.ANNUAL ? annualSubscription : monthlySubscription;
  } else {
    // Gift
    planPricing.price = planType.totalPrice;
    planPricing.subscription = `${planType.subscriptionLength} ${planType.subscriptionLengthUnit}`;
  }

  let addOnsSubtotal = 0.0;

  if (upgradingAddOns) {
    addOnsSubtotal = upgradingAddOns.subtotal;
  } else if (addOnPrice) {
    addOnsSubtotal = addOnPrice;
  }

  // Promotions cannot be applied to AddOns at this time
  const discount = promotion
    ? getDiscount(Number(planPricing.price), promotion)
    : 0;

  const combinedSubtotal = Number(planPricing.price) + Number(addOnsSubtotal);

  const calculatedTotal = combinedSubtotal - discount;
  // Price calculation logic END

  // Show Promotions if present
  const discountRow = promotion && (
    <div className={styles.discount}>
      <span>{promotion.description}</span>
      <span>-{formatCurrency(discount)}</span>
    </div>
  );

  const siteName = selectedSite?.friendlyName ?? ROOT_SITE_IDENTIFIER;

  // Provides additional context about the selected AddOn(s)
  const addOnSubtextRow =
    showAddOnOnly &&
    addOn &&
    (bundle && bundle.length ? (
      <div className={styles.subText}>
        {bundle.map(addon => addon.name).join(" . ")}
      </div>
    ) : (
      <>
        {addOn.type === ADDON_MODAL_CONSTANTS.ALL_ACCESS && (
          <div className={styles.subText}>{readOnlyAccess}</div>
        )}
        <div className={styles.subText}>{`Added to your ${
          addOn.associatedSubscription
            ? addOn.associatedSubscription.name
            : siteName
        } subscription`}</div>
        {proratedPrice !== undefined && (
          <div className={styles.subText}>{prorated}</div>
        )}
      </>
    ));

  // Displays the applied tax, if any (some states don't require tax to be charged)
  const taxRow = !!tax && (
    <div className={styles.tax}>
      <span>{estimatedTax}</span>
      <span>{formatCurrency(tax)}</span>
    </div>
  );

  const renderIncludedAddOn = (addOn: AddOn): JSX.Element => (
    <>
      <div className={styles.includedAddOnPrice}>
        <span>{addOn.includedAddOn?.lineItem}</span>
        <span className={styles.rightPrice}>
          {formatCurrency(parseFloat(addOn.includedAddOn?.price || "0"))}
        </span>
      </div>
      <div className={styles.includedAddOnPricePaid}>
        <span>{includedWith(addOn.lineItem)}</span>
        <span className={styles.rightPrice}>{free}</span>
      </div>
    </>
  );

  // Show information about AddOns in the summary when upgrading
  const updatedAddOns =
    upgradingAddOns &&
    upgradingAddOns.upgradingAddOns.length > 0 &&
    upgradingAddOns.upgradingAddOns.map(addOn => (
      <Fragment key={`upgrade-item-${addOn.id}`}>
        <div className={styles.addOnPrice}>
          <div>{addOn.lineItem}</div>
          <div>{`$${addOn.price}`}</div>
        </div>
        {addOn?.includedAddOn && renderIncludedAddOn(addOn)}
      </Fragment>
    ));

  // Show payment method information
  const paymentType = <span>{isPayPal ? payPalAccount : creditCard}</span>;

  const paymentMethodSection = (
    <div className={styles.paymentMethod}>
      <span>{paymentMethod}</span>
      {isValid ? paymentType : <span>-</span>}
    </div>
  );

  // Show for registration flow with add ons selected
  const registrationAddOns = !showAddOnOnly && addOn && (
    <div className={styles.addOnPrice}>
      <span>{addOn.lineItem}</span>
      <span className={styles.rightPrice}>
        {formatCurrency(parseFloat(addOn.price))}
      </span>
    </div>
  );

  // Show for registration flow with add ons selected and an included add on
  const includedAddOns = addOn?.includedAddOn && renderIncludedAddOn(addOn);

  const requestGiftTax = (
    plan: GiftPlan,
    site: Site,
    address: TaxAddress
  ): void => {
    dispatch(
      getGiftTax(
        plan.subscriptionLengthUnit,
        site.id,
        address,
        // TODO: https://griosf.atlassian.net/browse/RVLS-7859
        // Format for tax estimation by converting to cents and use double bitwise NOT to strip off the decimal places
        ~~(planPricing.price * CENTS_PER_DOLLAR)
      )
    );
  };

  const requestTax = (
    planType: string,
    site: Site,
    address: TaxAddress
  ): void => {
    dispatch(
      getTax(
        planType,
        site.id,
        address,
        promoCodeValue,
        // TODO: https://griosf.atlassian.net/browse/RVLS-7859
        ~~(combinedSubtotal * CENTS_PER_DOLLAR)
      )
    );
  };

  // TODO: https://griosf.atlassian.net/browse/RVLS-7859
  return (
    <div
      className={styles.finalPricing}
      {...setData("subscription-pricing", "final-pricing-card")}
    >
      {showAddOnOnly && (
        <div className={styles.billingToday}>{billingToday}</div>
      )}
      <div className={styles.price}>
        <span {...setData("subscription-pricing", "final-pricing-sub-type")}>
          {planPricing.subscription}
        </span>
        <span
          className={styles.rightPrice}
          {...setData("subscription-pricing", "final-pricing-sub-price")}
        >
          {formatCurrency(planPricing.price)}
        </span>
      </div>
      {!(showAddOnOnly && addOn) && (
        <div
          className={styles.siteShortName}
          {...setData("subscription-pricing", "final-pricing-sub-site")}
        >
          {siteName}
        </div>
      )}
      {discountRow}
      {registrationAddOns}
      {addOnSubtextRow}
      {includedAddOns}
      {updatedAddOns}
      {taxRow}
      {paymentMethodSection}
      <div
        className={styles.total}
        {...setData("subscription-pricing", "final-pricing-total-label")}
      >
        <span>{total}</span>
        <span {...setData("subscription-pricing", "final-pricing-total-price")}>
          {formatCurrency(calculatedTotal + (tax || 0))}
        </span>
      </div>
    </div>
  );
};

export default SubscriptionPricing;
